import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Avatar from 'flarum/common/components/Avatar';
import Button from 'flarum/common/components/Button';
import JoinBattleModal from './JoinBattleModal';
import CancelOpenBattleConfirmModal from './CancelOpenBattleConfirmModal';
/**
 * System post for an "open" battle lobby (see ArenaBattle::STATUS_WAITING /
 * CreateBattleController::handleOpenBattle). Renders a "waiting for
 * opponent" state with a Join button for any eligible viewer, or — once
 * this same post's content has been mutated by JoinBattleController on the
 * server (see ArenaBattleOpenPost::markJoined()) — the same "vs" matchup
 * view ArenaBattleStartPost shows for a normal challenge.
 */
export default class ArenaBattleOpenPost extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "challengerUser", null);
    _defineProperty(this, "opponentUser", null);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.resolveUsers();
  }
  async resolveUsers() {
    const content = this.attrs.post.content();
    if (!content) return;
    const fromStore = id => {
      var _app$store$getById;
      return (_app$store$getById = app.store.getById('users', String(id))) != null ? _app$store$getById : null;
    };
    this.challengerUser = fromStore(content.challengerId);
    this.opponentUser = content.opponentId != null ? fromStore(content.opponentId) : null;
    const loads = [];
    if (!this.challengerUser) loads.push(app.store.find('users', content.challengerId).catch(() => null));
    if (!this.opponentUser && content.opponentId != null) loads.push(app.store.find('users', content.opponentId).catch(() => null));
    if (loads.length) {
      await Promise.all(loads);
      this.challengerUser = fromStore(content.challengerId);
      this.opponentUser = content.opponentId != null ? fromStore(content.opponentId) : null;
      m.redraw();
    }
  }

  /**
   * Avatar+name+win/loss/draw row — matches ArenaBattleStartPost's own
   * playerColumn() exactly. Used both for the "waiting" view's lone
   * challenger column (shown above the card, see view() below) and for the
   * "joined" view's side-by-side matchup, since once a lobby is joined it's
   * meant to look identical to a normal challenge's start post.
   */
  playerColumnWithStats(user, fallbackName) {
    var _user$attribute, _name$, _stats$wins, _stats$losses, _stats$draws;
    const name = (user == null ? void 0 : user.displayName()) || fallbackName;
    const stats = (_user$attribute = user == null ? void 0 : user.attribute('arenaStats')) != null ? _user$attribute : null;
    const profileUrl = user ? app.route('user', {
      username: user.username()
    }) : null;
    const nameEl = profileUrl ? m("a", {
      className: "ArenaBattleStartPost-playerName",
      href: profileUrl,
      onclick: e => {
        e.preventDefault();
        m.route.set(profileUrl);
      }
    }, name) : m("div", {
      className: "ArenaBattleStartPost-playerName"
    }, name);
    return m("div", {
      className: "ArenaBattleStartPost-player"
    }, m("div", {
      className: "ArenaBattleStartPost-avatar"
    }, user ? m(Avatar, {
      user: user
    }) : m("span", {
      className: "Avatar ArenaBattleStartPost-avatarFallback"
    }, ((_name$ = name[0]) != null ? _name$ : '?').toUpperCase())), nameEl, stats && m("div", {
      className: "ArenaBattleStartPost-playerStats"
    }, m("span", {
      className: "ArenaBattleStartPost-stat ArenaBattleStartPost-stat--wins",
      title: app.translator.trans('forumaker-arena.lib.stats.wins')
    }, m("i", {
      className: "fas fa-trophy"
    }), " ", (_stats$wins = stats.wins) != null ? _stats$wins : 0), m("span", {
      className: "ArenaBattleStartPost-stat ArenaBattleStartPost-stat--losses",
      title: app.translator.trans('forumaker-arena.lib.stats.losses')
    }, m("i", {
      className: "fas fa-skull"
    }), " ", (_stats$losses = stats.losses) != null ? _stats$losses : 0), m("span", {
      className: "ArenaBattleStartPost-stat ArenaBattleStartPost-stat--draws",
      title: app.translator.trans('forumaker-arena.lib.stats.draws')
    }, m("i", {
      className: "fas fa-handshake"
    }), " ", (_stats$draws = stats.draws) != null ? _stats$draws : 0)));
  }
  canJoin(content) {
    const actor = app.session.user;
    if (!actor || content.status !== 'waiting') return false;
    if (parseInt(actor.id()) === content.challengerId) return false;
    return true;
  }

  /** Guests can't join a battle, but the lobby is still open to them — show
   * a "log in to accept" button instead of hiding the action entirely. */
  canJoinAsGuest(content) {
    return !app.session.user && content.status === 'waiting';
  }

  /** True for the lobby's own creator, who can cancel it instead of joining. */
  isOwner(content) {
    const actor = app.session.user;
    if (!actor || content.status !== 'waiting') return false;
    return parseInt(actor.id()) === content.challengerId;
  }
  view() {
    const content = this.attrs.post.content();
    if (!content) return null;
    if (content.status === 'joined') {
      var _content$opponentName;
      // Deliberately just "ArenaBattleStartPost EventPost" — no
      // ArenaBattleOpenPost class here. That class carries its own bordered/
      // tinted-background card (see the "waiting" render below), and stacking
      // it underneath ArenaBattleStartPost-wrap's own card produced a double
      // box: a colored backdrop peeking out around the real card. Once a
      // lobby is joined it's meant to look exactly like a normal challenge's
      // start post, including the win/loss/draw row under each avatar (see
      // playerColumnWithStats()).
      return m("div", {
        className: "ArenaBattleStartPost EventPost"
      }, m("div", {
        className: "ArenaBattleStartPost-wrap"
      }, m("div", {
        className: "ArenaBattleStartPost-versus"
      }, this.playerColumnWithStats(this.challengerUser, content.challengerName), m("span", {
        className: "ArenaBattleStartPost-vs"
      }, "VS"), this.playerColumnWithStats(this.opponentUser, (_content$opponentName = content.opponentName) != null ? _content$opponentName : ''))), m("div", {
        className: "ArenaStartPost"
      }, m("div", {
        className: "ArenaStartPost-icon"
      }, m("i", {
        className: "fas fa-swords"
      })), m("div", {
        className: "ArenaStartPost-body"
      }, m("div", {
        className: "ArenaStartPost-title"
      }, app.translator.trans('forumaker-arena.forum.battle_start.first_turn', {
        name: m("strong", null, content.challengerName)
      })), m("div", {
        className: "ArenaStartPost-subtitle"
      }, app.translator.trans('forumaker-arena.forum.battle_start.first_turn_subtitle')))));
    }
    return m("div", {
      className: "ArenaBattleOpenPost EventPost"
    }, m("div", {
      className: "ArenaBattleOpenPost-challenger"
    }, this.playerColumnWithStats(this.challengerUser, content.challengerName)), m("div", {
      className: "ArenaBattleOpenPost-wrap"
    }, m("div", {
      className: "ArenaBattleOpenPost-info"
    }, m("div", {
      className: "ArenaBattleOpenPost-icon"
    }, m("i", {
      className: "fas fa-hourglass-half"
    })), m("div", {
      className: "ArenaBattleOpenPost-body"
    }, m("div", {
      className: "ArenaBattleOpenPost-title"
    }, app.translator.trans('forumaker-arena.forum.battle_open.title', {
      name: m("strong", null, content.challengerName)
    }))), m("div", {
      className: "ArenaBattleOpenPost-actions"
    }, this.canJoin(content) && m(Button, {
      className: "Button Button--primary ArenaBattleOpenPost-joinBtn",
      onclick: () => app.modal.show(JoinBattleModal, {
        battleId: content.battleId,
        challengerName: content.challengerName
      })
    }, m("i", {
      className: "fas fa-user-plus"
    }), " ", app.translator.trans('forumaker-arena.forum.battle_open.join_btn')), this.canJoinAsGuest(content) && m(Button, {
      className: "Button Button--primary ArenaBattleOpenPost-joinBtn",
      onclick: () => app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'))
    }, m("i", {
      className: "fas fa-user-plus"
    }), " ", app.translator.trans('forumaker-arena.forum.battle_open.join_guest_btn')), this.isOwner(content) && m(Button, {
      className: "Button ArenaBattleOpenPost-cancelBtn",
      onclick: () => app.modal.show(CancelOpenBattleConfirmModal, {
        battleId: content.battleId,
        onCancelled: () => m.route.set(app.route('index'))
      })
    }, m("i", {
      className: "fas fa-door-closed"
    }), " ", app.translator.trans('forumaker-arena.forum.battle_open.cancel_btn'))))));
  }
}
flarum.reg.add('forumaker-arena', 'forum/components/ArenaBattleOpenPost', ArenaBattleOpenPost);